import { normalizeToIsoZMidnight } from '../../utils/dateNormalization'
import {
  buildImportSession,
  buildImportValidationResults,
  createImportDedupeIssue,
  createImportRowIssue,
  buildImportPreviewRecord,
  buildImportPreviewRows,
  mapImportHeaders,
  parseCsvImportTable,
  type ImportMappedHeader,
  type ImportRecordResult,
  type ImportSession,
  type ImportSourceFile,
  type ParsedImportTable
} from '../../utils/importTable'
import { ensureUniqueImportedRowId, toStableImportIdString } from '../../utils/importRowIdentity'
import { resolveCanonicalLotKey } from '../../utils/lotIdentity'
import { toDayKeyDateOnly } from '../../utils/productionFeedKgDerivation'
import type { WaterDataRow, WaterLogMetadata } from '../../utils/waterTable'
import { reconcileImportedWaterLot } from './importLotReconciliation'

type WaterImportLot = {
  id: string
  lotKey: string
  stage?: string
}

type WaterImportTargetKey = 'water'
type WaterImportContext = {
  farmId: string
  productionUnitId: string
}
type WaterMappedHeader = ImportMappedHeader<keyof WaterDataRow>

export type WaterImportRowResult = ImportRecordResult<WaterDataRow, keyof WaterDataRow>
export type WaterImportSession = ImportSession<WaterDataRow, keyof WaterDataRow, WaterImportTargetKey, WaterImportContext>

const normalizeWaterImportHeader = (value: string): string => (
  // #46: strip accent marks BEFORE lowercasing so Spanish-locale headers like
  // "Oxígeno", "Saturación", "Salinidad" normalize to their accent-less
  // forms and can match aliases without listing every diacritic variant.
  value
    .toString()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
)

const WATER_IMPORT_FIELD_MAP: Record<string, keyof WaterDataRow> = {
  date: 'date',
  lot: 'lot',
  temperature: 'temperature',
  // #40 follow-on: accept common unit-suffixed and shorthand temperature headers
  // that third-party spreadsheets emit (e.g. "Temperature C", "Temp °C", "T°C").
  temperaturec: 'temperature',
  temperaturef: 'temperature',
  temperaturecelsius: 'temperature',
  tempc: 'temperature',
  tempf: 'temperature',
  temp: 'temperature',
  t: 'temperature',
  ph: 'ph',
  // Common pH header variants like "pH (units)" or "pH water".
  phunits: 'ph',
  phwater: 'ph',
  dissolvedoxygen: 'manualDissolvedOxygen',
  doxy: 'manualDissolvedOxygen',
  dissolvedoxygensaturation: 'manualDissolvedOxygenSaturation',
  manualdissolvedoxygen: 'manualDissolvedOxygen',
  manualdissolvedoxygensaturation: 'manualDissolvedOxygenSaturation',
  salinity: 'salinity',
  notes: 'notes',
  id: 'id',
  // #46: Spanish-locale aliases. Surfaced by the user's `test 2 water 102.csv`
  // (`Fecha;Lot;Temperature;pH`). Diacritics are stripped during the
  // normalizer above, so accented variants ("Oxígeno", "Saturación")
  // resolve to the accent-less form here.
  fecha: 'date',
  fechahora: 'date',
  lote: 'lot',
  temperatura: 'temperature',
  temperaturac: 'temperature',
  temperaturacelsius: 'temperature',
  oxigeno: 'manualDissolvedOxygen',
  oxigenodisuelto: 'manualDissolvedOxygen',
  od: 'manualDissolvedOxygen',
  saturacion: 'manualDissolvedOxygenSaturation',
  saturaciondeoxigeno: 'manualDissolvedOxygenSaturation',
  saturacionoxigeno: 'manualDissolvedOxygenSaturation',
  saturacionod: 'manualDissolvedOxygenSaturation',
  salinidad: 'salinity',
  notas: 'notes',
  nota: 'notes',
  observaciones: 'notes',
  observacion: 'notes',
  comentarios: 'notes',
  comentario: 'notes'
}

const parseOptionalImportedNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  // #40: accept European-style comma decimals ("7,00" → 7.00) that third-party
  // spreadsheet exports often emit. Only swap a single comma when there is no
  // dot already and the value looks like a single numeric literal — keeps
  // thousands-separator strings ("1.234") untouched.
  let raw = value.trim()
  if (!raw) return null
  if (raw.includes(',') && !raw.includes('.') && /^-?\d+(,\d+)?$/.test(raw)) {
    raw = raw.replace(',', '.')
  }
  const parsed = Number.parseFloat(raw)
  return Number.isFinite(parsed) ? parsed : null
}

const buildWaterMappedHeaders = (table: ParsedImportTable): WaterMappedHeader[] => {
  return mapImportHeaders({
    headers: table.headers,
    normalizeHeader: normalizeWaterImportHeader,
    targetByNormalizedHeader: WATER_IMPORT_FIELD_MAP
  })
}

const validateWaterImportRecord = (args: {
  previewRow: Parameters<typeof createImportRowIssue<keyof WaterDataRow>>[0]['previewRow']
  record: Record<string, unknown>
}) => {
  const issues = []
  const rawDate = typeof args.record.date === 'string' ? args.record.date.trim() : ''

  if (!rawDate) {
    issues.push(createImportRowIssue({
      previewRow: args.previewRow,
      targetField: 'date',
      code: 'water-date-required',
      severity: 'error',
      message: 'Date is required.'
    }))
    return issues
  }

  const normalizedDate = normalizeToIsoZMidnight(rawDate)
  if (!normalizedDate.ok) {
    issues.push(createImportRowIssue({
      previewRow: args.previewRow,
      targetField: 'date',
      code: 'water-date-invalid',
      severity: 'error',
      message: 'Date must be a recognizable date.'
    }))
  }

  return issues
}

const buildImportedWaterMetadata = (sourceFile?: ImportSourceFile | null): WaterLogMetadata => {
  let batchId: string | undefined
  if (sourceFile?.name) {
    batchId = sourceFile.name
    if (sourceFile.lastModifiedMs) {
      batchId = `${sourceFile.name}:${sourceFile.lastModifiedMs}`
    }
  }

  return {
    sourceType: 'csv',
    ...(batchId ? { batchId } : {})
  }
}

const buildWaterImportArtifactsFromCsvText = (args: {
  text: string
  existingRows: WaterDataRow[]
  availableLots: WaterImportLot[]
  lotKeyById: Map<string, string>
  sourceFile?: ImportSourceFile | null
  /** v2.1 #47: farm timezone for `(lot, day)` dedup key derivation. */
  timeZone?: string
}): {
  table: ParsedImportTable
  mappedHeaders: WaterMappedHeader[]
  results: WaterImportRowResult[]
} | null => {
  const parsedTable = parseCsvImportTable(args.text)
  if (!parsedTable) return null

  const mappedHeaders = buildWaterMappedHeaders(parsedTable)
  const importedMetadata = buildImportedWaterMetadata(args.sourceFile)
  const previewRows = buildImportPreviewRows({
    table: parsedTable,
    mappedHeaders,
    normalizeCellValue: (value) => value.replaceAll(',', '.')
  })
  const existingIds = new Set(args.existingRows.map((row) => row.id))
  const importedIds = new Set<string>()
  const dayKeyOptions = args.timeZone ? { timeZone: args.timeZone } : undefined
  // #42: preventive (lot, day) dedup for Water imports. Mirrors the Database
  // (#41) behavior — same canonical key. Now that the Water save plan appends
  // rather than replaces (#38), duplicates can be created by an import, so we
  // warn the user at review time. There is no curative "fuse" button for
  // Water yet, so the user must clean up manually after import if desired.
  // v2.1 #47: when `args.timeZone` is provided, derive the day key in that
  // zone so we match the operator's local calendar rather than UTC.
  const existingLotDayKeys = new Set<string>()
  for (const row of args.existingRows) {
    const lotToken = resolveCanonicalLotKey(row.lotId, row.lot)
    if (lotToken === 'unknown-lot') continue
    const dayKey = toDayKeyDateOnly(row.date ?? '', dayKeyOptions)
    if (!dayKey) continue
    existingLotDayKeys.add(`${dayKey}::${lotToken}`)
  }
  const importedLotDayKeys = new Set<string>()

  const results = buildImportValidationResults({
    previewRows,
    buildRecord: (previewRow) => buildImportPreviewRecord({ previewRow }),
    validateRecord: ({ previewRow, record }) => validateWaterImportRecord({ previewRow, record })
  }).map((validationResult) => {
    const row = validationResult.record
    const dedupeResult = ensureUniqueImportedRowId({
      row,
      existingIds,
      importedIds,
      idPrefix: 'water'
    })
    const dedupeIssue = createImportDedupeIssue({
      previewRow: validationResult.previewRow,
      targetField: 'id',
      dedupeResult
    })

    row.temperature = parseOptionalImportedNumber(row.temperature)
    row.ph = parseOptionalImportedNumber(row.ph)
    row.dissolvedOxygen = parseOptionalImportedNumber(row.dissolvedOxygen)
    row.dissolvedOxygenSaturation = parseOptionalImportedNumber(row.dissolvedOxygenSaturation)
    row.manualDissolvedOxygen = parseOptionalImportedNumber(row.manualDissolvedOxygen)
    row.manualDissolvedOxygenSaturation = parseOptionalImportedNumber(row.manualDissolvedOxygenSaturation)
    row.salinity = parseOptionalImportedNumber(row.salinity)

    if (typeof row.date === 'string') {
      const normalized = normalizeToIsoZMidnight(row.date)
      if (normalized.ok) {
        row.date = normalized.isoZ
      }
    }

    reconcileImportedWaterLot({ row, lots: args.availableLots, lotKeyById: args.lotKeyById })

    const typedRow: WaterDataRow = {
      id: toStableImportIdString(row.id),
      date: typeof row.date === 'string' ? row.date : '',
      lot: typeof row.lot === 'string' ? row.lot : '',
      lotId: typeof row.lotId === 'string' ? row.lotId : '',
      unmappedLot: typeof row.unmappedLot === 'string' ? row.unmappedLot : '',
      temperature: parseOptionalImportedNumber(row.temperature),
      ph: parseOptionalImportedNumber(row.ph),
      dissolvedOxygen: parseOptionalImportedNumber(row.dissolvedOxygen),
      dissolvedOxygenSaturation: parseOptionalImportedNumber(row.dissolvedOxygenSaturation),
      manualDissolvedOxygen: parseOptionalImportedNumber(row.manualDissolvedOxygen),
      manualDissolvedOxygenSaturation: parseOptionalImportedNumber(row.manualDissolvedOxygenSaturation),
      salinity: parseOptionalImportedNumber(row.salinity),
      notes: typeof row.notes === 'string' ? row.notes : '',
      metadata: importedMetadata
    }

    // #42: preventive (lot, day) duplicate warning.
    const issues = dedupeIssue ? [...validationResult.issues, dedupeIssue] : [...validationResult.issues]
    const lotToken = resolveCanonicalLotKey(typedRow.lotId, typedRow.lot)
    const dayKey = toDayKeyDateOnly(typedRow.date, dayKeyOptions)
    if (lotToken !== 'unknown-lot' && dayKey) {
      const lotDayKey = `${dayKey}::${lotToken}`
      const collidesWithExisting = existingLotDayKeys.has(lotDayKey)
      const collidesWithImported = importedLotDayKeys.has(lotDayKey)
      if (collidesWithExisting || collidesWithImported) {
        issues.push(createImportRowIssue({
          previewRow: validationResult.previewRow,
          targetField: 'date',
          code: 'water-duplicate-lot-day',
          severity: 'warning',
          message: collidesWithExisting
            ? 'A reading for this lot and day already exists. The import will create a duplicate row.'
            : 'Another imported row already targets this lot and day. The import will create a duplicate row.'
        }))
      }
      importedLotDayKeys.add(lotDayKey)
    }

    return {
      ...validationResult,
      record: typedRow,
      issues,
      dedupeResult
    }
  })

  return {
    table: parsedTable,
    mappedHeaders,
    results
  }
}

export const buildImportedWaterRowsFromCsvText = (args: {
  text: string
  existingRows: WaterDataRow[]
  availableLots: WaterImportLot[]
  lotKeyById: Map<string, string>
  sourceFile?: ImportSourceFile | null
  timeZone?: string
}): WaterDataRow[] => {
  return buildImportedWaterRowResultsFromCsvText(args).map((result) => result.record)
}

export const buildImportedWaterRowResultsFromCsvText = (args: {
  text: string
  existingRows: WaterDataRow[]
  availableLots: WaterImportLot[]
  lotKeyById: Map<string, string>
  sourceFile?: ImportSourceFile | null
  timeZone?: string
}): WaterImportRowResult[] => {
  return buildWaterImportArtifactsFromCsvText(args)?.results ?? []
}

export const buildWaterImportSessionFromCsvText = (args: {
  text: string
  existingRows: WaterDataRow[]
  availableLots: WaterImportLot[]
  lotKeyById: Map<string, string>
  sourceFile?: ImportSourceFile | null
  assignedContext?: WaterImportContext | null
  targetLabel?: string
  /** v2.1 #47: farm timezone for `(lot, day)` dedup key derivation. */
  timeZone?: string
}): WaterImportSession | null => {
  const artifacts = buildWaterImportArtifactsFromCsvText(args)
  if (!artifacts) return null

  return buildImportSession({
    sourceFile: args.sourceFile ?? null,
    table: artifacts.table,
    selectedTarget: {
      targetKey: 'water',
      label: args.targetLabel ?? 'Water'
    },
    assignedContext: args.assignedContext ?? null,
    mappedHeaders: artifacts.mappedHeaders,
    results: artifacts.results
  })
}